import {ReactElement} from "react";
import {RoundRobinStanding, TournamentTeam} from "@/competitions/tournaments/types";

interface RoundRobinStandingsProps {
    standings: RoundRobinStanding[];
    /** Full team data array, keyed by id */
    teams?: Record<string, TournamentTeam>;
    /** Legacy support - team ID to name mapping */
    teamNames?: Record<string, string>;
    /** The viewer's own team id — cyan YOU row per the mockups. */
    highlightTeamId?: string;
}

/**
 * Round robin standings table — the mockups' `.standings` treatment: rank,
 * logo+name team cell (cyan YOU row), mono W–L record, signed differential and
 * points, ordered by the engine's deterministic tiebreaker chain
 * (points → head-to-head → differential → points scored → seed).
 */
export default function RoundRobinStandings({
    standings,
    teams,
    teamNames,
    highlightTeamId,
}: RoundRobinStandingsProps): ReactElement {
    const nameFor = (s: RoundRobinStanding): string =>
        teams?.[s.teamId]?.name ?? teamNames?.[s.teamId] ?? s.name ?? "Unknown";

    return (
        <table className="bracket-viewer__standings">
            <thead>
                <tr>
                    <th className="bracket-viewer__standings-rank">#</th>
                    <th className="bracket-viewer__standings-team">Team</th>
                    <th>W–L</th>
                    <th>Maps</th>
                    <th>Diff</th>
                    <th>Pts</th>
                </tr>
            </thead>
            <tbody>
                {standings.map((s) => {
                    const isYou = highlightTeamId && s.teamId === highlightTeamId;
                    const logoUrl = teams?.[s.teamId]?.logoUrl;
                    return (
                        <tr key={s.teamId} className={isYou ? "bracket-viewer__standings-row--you" : undefined}>
                            <td className="bracket-viewer__standings-rank">{s.rank}</td>
                            <td className="bracket-viewer__standings-team">
                                <span className="bracket-viewer__standings-cell">
                                    {logoUrl && <img className="bracket-viewer__standings-logo" src={logoUrl} alt="" />}
                                    <span className="bracket-viewer__standings-name">{nameFor(s)}</span>
                                    {isYou && <span className="bracket-viewer__standings-you">YOU</span>}
                                </span>
                            </td>
                            <td className="bracket-viewer__standings-wl">{s.wins}–{s.losses}</td>
                            <td>{s.pointsFor}–{s.pointsAgainst}</td>
                            <td className={s.pointDifferential > 0 ? "bracket-viewer__standings-pos" : s.pointDifferential < 0 ? "bracket-viewer__standings-neg" : undefined}>
                                {s.pointDifferential > 0 ? `+${s.pointDifferential}` : s.pointDifferential}
                            </td>
                            <td className="bracket-viewer__standings-pts">{s.points}</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}
